import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import ChatWindow from '../components/ChatWindow';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import GUIDED_MODULES from '../data/guidedChatModules';

/* ── Icon helper ── */
const ICONS = {
  compass: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/></svg>,
  briefcase: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>,
  target: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>,
  building: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="2" width="16" height="20" rx="2" ry="2"/><path d="M9 22v-4h6v4"/><line x1="8" y1="6" x2="8" y2="6.01"/><line x1="16" y1="6" x2="16" y2="6.01"/><line x1="12" y1="6" x2="12" y2="6.01"/><line x1="8" y1="10" x2="8" y2="10.01"/><line x1="16" y1="10" x2="16" y2="10.01"/><line x1="12" y1="10" x2="12" y2="10.01"/><line x1="8" y1="14" x2="8" y2="14.01"/><line x1="16" y1="14" x2="16" y2="14.01"/><line x1="12" y1="14" x2="12" y2="14.01"/></svg>,
  gavel: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14.5 2l6 6-8 8-6-6z"/><path d="M4 14l2-2"/><path d="M2 22l4-4"/><path d="M14.5 2L18 5.5"/></svg>,
  graduation: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 10l-10-5L2 10l10 5 10-5z"/><path d="M6 12v5c0 2 3 3 6 3s6-1 6-3v-5"/><line x1="22" y1="10" x2="22" y2="16"/></svg>,
};

const MAX_FOLLOWUPS = 3;

export default function GuidedChat() {
  const { module: moduleSlug } = useParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const mod = GUIDED_MODULES[moduleSlug];

  const storageKey = `guidedChat_${moduleSlug}`;

  const savedState = React.useMemo(() => {
    try {
      const raw = sessionStorage.getItem(storageKey);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── Phase: 'intro' → 'intake' → 'chat' ── */
  const [phase, setPhase] = useState(savedState?.phase ?? 'intro');

  /* Intake conversation (intro question + AI follow-ups) */
  const [intakeMessages, setIntakeMessages] = useState(savedState?.intakeMessages ?? []);
  const [followUpCount, setFollowUpCount] = useState(savedState?.followUpCount ?? 0);
  const [intakeInput, setIntakeInput] = useState('');

  /* Chat state (after intake) */
  const [openingMessage, setOpeningMessage] = useState(savedState?.openingMessage ?? null);
  const [messages, setMessages] = useState(savedState?.messages ?? []);
  const [chatInput, setChatInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tokenBalance, setTokenBalance] = useState(user?.tokenBalance ?? 0);
  const [toast, setToast] = useState('');
  const textareaRef = useRef(null);
  const intakeRef = useRef(null);
  const bottomRef = useRef(null);
  const WORD_LIMIT = 40;

  // Persist state
  useEffect(() => {
    const state = { phase, intakeMessages, followUpCount, openingMessage, messages };
    try { sessionStorage.setItem(storageKey, JSON.stringify(state)); } catch {}
  }, [phase, intakeMessages, followUpCount, openingMessage, messages, storageKey]);

  useEffect(() => {
    if (user?.tokenBalance !== undefined) setTokenBalance(user.tokenBalance);
  }, [user?.tokenBalance]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [intakeMessages, openingMessage, messages, loading]);

  useEffect(() => {
    if (phase === 'intro' || phase === 'intake') {
      setTimeout(() => intakeRef.current?.focus(), 100);
    }
  }, [phase, intakeMessages]);

  if (!mod) {
    return (
      <div style={s.page}>
        <div style={s.notFound}>
          <h2>Module not found</h2>
          <Link to="/" style={s.backLink}>Back to Dashboard</Link>
        </div>
      </div>
    );
  }

  const isExhausted = tokenBalance <= 0;

  /* ── Build student context summary from intake messages ── */
  const buildStudentContext = (msgs) => {
    const studentMsgs = msgs.filter(m => m.role === 'user').map(m => m.content);
    return studentMsgs.join('\n');
  };

  /* ── Intro: student answers the first question ── */
  const handleIntroSubmit = () => {
    const val = intakeInput.trim();
    if (!val || loading) return;

    const newMessages = [{ role: 'user', content: val }];
    setIntakeMessages(newMessages);
    setIntakeInput('');
    setPhase('intake');
    // Ask AI for first follow-up
    askIntakeFollowUp(newMessages, 0);
  };

  /* ── Ask AI for a follow-up question ── */
  const askIntakeFollowUp = async (conversationSoFar, currentCount) => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.post('/ask/guided-intake', {
        intakeSystemPrompt: mod.intakeSystemPrompt,
        conversation: conversationSoFar,
      }, { timeout: 30000 });

      const updatedMessages = [...conversationSoFar, { role: 'assistant', content: data.answer }];
      setIntakeMessages(updatedMessages);
      setFollowUpCount(currentCount + 1);

      if (data.tokenBalance !== undefined) {
        setTokenBalance(data.tokenBalance);
        refreshUser();
      }
    } catch (err) {
      const code = err.response?.data?.code;
      if (code === 'TOKEN_EXHAUSTED') {
        setTokenBalance(0);
        setError('You have run out of tokens.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  /* ── Student answers an intake follow-up ── */
  const handleIntakeReply = () => {
    const val = intakeInput.trim();
    if (!val || loading) return;

    const updatedMessages = [...intakeMessages, { role: 'user', content: val }];
    setIntakeMessages(updatedMessages);
    setIntakeInput('');

    if (followUpCount >= MAX_FOLLOWUPS) {
      // Max follow-ups reached, transition to chat
      transitionToChat(updatedMessages);
    } else {
      // Ask next follow-up
      askIntakeFollowUp(updatedMessages, followUpCount);
    }
  };

  /* ── Skip remaining follow-ups and go to chat ── */
  const handleStartChat = () => {
    transitionToChat(intakeMessages);
  };

  /* ── Transition from intake to main chat ── */
  const transitionToChat = async (allIntakeMessages) => {
    setPhase('chat');
    setLoading(true);
    setError('');
    try {
      const studentContext = buildStudentContext(allIntakeMessages);
      const firstQuestion = `[Student Context] Bucket: ${mod.title}\n\nHere is what the student shared during intake:\n${studentContext}\n\nPlease now begin the mentoring conversation. Warmly acknowledge what the student has shared (without repeating it back robotically). Signal understanding of where the student is. Then invite the student to ask their specific question or begin giving tailored advice.`;

      const { data } = await api.post('/ask/guided', {
        question: firstQuestion,
        moduleContext: mod.chatSystemPrompt,
        userAnswers: { context: studentContext },
      }, { timeout: 60000 });

      setOpeningMessage(data);
      if (data.tokenBalance !== undefined) {
        setTokenBalance(data.tokenBalance);
        refreshUser();
      }
    } catch (err) {
      const code = err.response?.data?.code;
      if (code === 'TOKEN_EXHAUSTED') {
        setTokenBalance(0);
        setError('You have run out of tokens.');
      } else {
        setError('Failed to start conversation. Please try again.');
      }
    } finally {
      setLoading(false);
      textareaRef.current?.focus();
    }
  };

  /* ── Chat follow-up ── */
  const askFollowUp = async (question) => {
    if (!question || loading || isExhausted) return;
    setChatInput('');
    setError('');
    setLoading(true);
    try {
      const studentContext = buildStudentContext(intakeMessages);
      const { data } = await api.post('/ask/guided', {
        question,
        moduleContext: mod.chatSystemPrompt,
        userAnswers: { context: studentContext },
      }, { timeout: 60000 });
      setMessages(prev => [...prev, data]);
      if (data.tokenBalance !== undefined) {
        setTokenBalance(data.tokenBalance);
        refreshUser();
      }
    } catch (err) {
      const code = err.response?.data?.code;
      if (code === 'TOKEN_EXHAUSTED') {
        setTokenBalance(0);
        setError('You have run out of tokens.');
      } else {
        setError('Failed to get an answer. Please try again.');
      }
    } finally {
      setLoading(false);
      textareaRef.current?.focus();
    }
  };

  const wordCount = chatInput.trim() ? chatInput.trim().split(/\s+/).length : 0;
  const overLimit = wordCount > WORD_LIMIT;

  const handleChatSubmit = (e) => {
    e.preventDefault();
    if (overLimit) {
      setToast(`Please keep your question under ${WORD_LIMIT} words`);
      setTimeout(() => setToast(''), 3000);
      return;
    }
    askFollowUp(chatInput.trim());
  };

  const handleChatKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleChatSubmit(e); }
  };

  const handleIntakeKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (phase === 'intro') handleIntroSubmit();
      else handleIntakeReply();
    }
  };

  const balanceColor = tokenBalance > 10000 ? '#4ade80' : tokenBalance > 2000 ? '#fbbf24' : '#f87171';

  /* ═══════════════════════════════════════════════════════
     RENDER: Intro + Intake Phase (conversational)
     ═══════════════════════════════════════════════════════ */
  if (phase === 'intro' || phase === 'intake') {
    return (
      <div style={s.page}>
        <div style={s.chatContainer}>
          {/* Header */}
          <div style={{ ...s.chatHeader, background: mod.color }}>
            <div style={s.chatHeaderLeft}>
              <button onClick={() => navigate('/')} style={s.headerBackBtn}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 18 9 12 15 6"/>
                </svg>
              </button>
              <div style={s.chatHeaderIcon}>{ICONS[mod.icon]}</div>
              <div>
                <h1 style={s.chatTitle}>{mod.title}</h1>
                <p style={s.chatSubtitle}>{mod.subtitle}</p>
              </div>
            </div>
          </div>

          {/* Conversation area */}
          <div style={s.chatArea}>
            <div style={s.chatScroll}>
              {/* AI intro question */}
              <div style={s.aiRow}>
                <img src="/nitin-potdar-profile.png" alt="Nitin" style={s.aiAva} />
                <div style={s.aiBubble}>
                  <p style={{ ...s.mdP, margin: 0 }}>{mod.introQuestion}</p>
                </div>
              </div>

              {/* Intake conversation messages */}
              {intakeMessages.map((msg, i) => (
                <div key={i}>
                  {msg.role === 'user' ? (
                    <div style={s.userRow}>
                      <div style={s.userBubble}>
                        <p style={{ ...s.mdP, margin: 0 }}>{msg.content}</p>
                      </div>
                    </div>
                  ) : (
                    <div style={s.aiRow}>
                      <img src="/nitin-potdar-profile.png" alt="Nitin" style={s.aiAva} />
                      <div style={s.aiBubble}>
                        <p style={{ ...s.mdP, margin: 0 }}>{msg.content}</p>
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {/* Loading indicator */}
              {loading && (
                <div style={s.aiRow}>
                  <img src="/nitin-potdar-profile.png" alt="Nitin" style={s.aiAva} />
                  <div style={{ ...s.aiBubble, padding: '14px 18px' }}>
                    <div style={s.typing}>
                      {[0, 0.18, 0.36].map((d, i) => (
                        <span key={i} style={{ ...s.dot, animationDelay: `${d}s` }} />
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <div ref={bottomRef} />
            </div>
          </div>

          {/* Error */}
          {error && (
            <div style={s.errorBar}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              {error}
            </div>
          )}

          {/* Skip to chat button (visible after at least 1 follow-up answered) */}
          {phase === 'intake' && followUpCount >= 1 && !loading && intakeMessages[intakeMessages.length - 1]?.role === 'assistant' && (
            <div style={s.skipBar}>
              <button onClick={handleStartChat} style={{ ...s.skipBtn, borderColor: `${mod.color}40`, color: mod.color }}>
                Skip to conversation →
              </button>
            </div>
          )}

          {/* Input */}
          <div style={s.inputArea}>
            <div style={s.inputWrap}>
              <textarea
                ref={intakeRef}
                value={intakeInput}
                onChange={(e) => setIntakeInput(e.target.value)}
                onKeyDown={handleIntakeKeyDown}
                placeholder={phase === 'intro' ? 'Type your introduction here...' : 'Type your answer...'}
                style={s.textarea}
                rows={2}
                disabled={loading}
              />
              <button
                onClick={phase === 'intro' ? handleIntroSubmit : handleIntakeReply}
                disabled={loading || !intakeInput.trim()}
                style={{
                  ...s.sendBtn,
                  background: intakeInput.trim() && !loading ? mod.color : 'var(--border)',
                  cursor: intakeInput.trim() && !loading ? 'pointer' : 'not-allowed',
                }}
              >
                {loading
                  ? <span style={s.spinner2} />
                  : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/>
                    </svg>
                }
              </button>
            </div>
            <p style={s.inputHint}>Enter to send</p>
          </div>
        </div>
      </div>
    );
  }

  /* ═══════════════════════════════════════════════════════
     RENDER: Main Chat Phase
     ═══════════════════════════════════════════════════════ */
  return (
    <div style={s.page}>
      <div style={s.chatContainer}>
        {/* Header */}
        <div style={{ ...s.chatHeader, background: mod.color }}>
          <div style={s.chatHeaderLeft}>
            <Link to="/" style={s.headerBackBtn}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6"/>
              </svg>
            </Link>
            <div style={s.chatHeaderIcon}>{ICONS[mod.icon]}</div>
            <div>
              <h1 style={s.chatTitle}>{mod.title}</h1>
              <p style={s.chatSubtitle}>{mod.subtitle}</p>
            </div>
          </div>
          <div style={{ ...s.balancePill, borderColor: `${balanceColor}40` }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={balanceColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
            </svg>
            <span style={{ ...s.balanceNum, color: balanceColor }}>
              {tokenBalance.toLocaleString()}
            </span>
            <span style={s.balanceLabel}>tokens</span>
          </div>
        </div>

        {/* Back to topics link */}
        <div style={s.topicBar}>
          <Link to="/" style={s.topicLink}>← Back to topics</Link>
        </div>

        {/* Token exhausted banner */}
        {isExhausted && (
          <div style={s.exhaustedBanner}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            <div>
              <strong style={{ display: 'block', marginBottom: '2px' }}>Token balance exhausted</strong>
              <span style={{ fontSize: '12px', opacity: 0.85 }}>Contact an admin or purchase tokens to continue chatting.</span>
            </div>
          </div>
        )}

        {/* Chat area */}
        <div style={s.chatArea}>
          <div style={s.chatScroll}>
            {/* AI opening message */}
            {openingMessage && (
              <div style={s.openingBlock}>
                <div style={s.aiRow}>
                  <img src="/nitin-potdar-profile.png" alt="Nitin" style={s.aiAva} />
                  <div style={s.aiBubble}>
                    <div style={s.mdWrapper}>
                      <ReactMarkdown
                        components={{
                          p:      ({ children }) => <p style={s.mdP}>{children}</p>,
                          ul:     ({ children }) => <ul style={s.mdUl}>{children}</ul>,
                          ol:     ({ children }) => <ol style={s.mdOl}>{children}</ol>,
                          li:     ({ children }) => <li style={s.mdLi}>{children}</li>,
                          strong: ({ children }) => <strong style={s.mdStrong}>{children}</strong>,
                          h1:     ({ children }) => <p style={s.mdH}>{children}</p>,
                          h2:     ({ children }) => <p style={s.mdH}>{children}</p>,
                          h3:     ({ children }) => <p style={s.mdH3}>{children}</p>,
                        }}
                      >
                        {openingMessage.answer}
                      </ReactMarkdown>
                    </div>
                    <span style={s.ts}>
                      {new Date(openingMessage.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                </div>
                {openingMessage.suggestedQuestions?.length > 0 && messages.length === 0 && !loading && (
                  <div style={s.suggestRow}>
                    <span style={s.suggestLabel}>Ask next</span>
                    <div style={s.suggestChips}>
                      {openingMessage.suggestedQuestions.map((q, i) => (
                        <button key={i} style={s.suggestChip} onClick={() => askFollowUp(q)}>{q}</button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Subsequent messages */}
            {messages.length > 0 && (
              <ChatWindow
                messages={messages}
                loading={loading}
                onSuggest={(q) => askFollowUp(q)}
                userName={user?.name ? user.name.split(' ')[0] : ''}
              />
            )}

            {/* Loading (first load) */}
            {!openingMessage && loading && (
              <div style={s.loadingBlock}>
                <div style={s.aiRow}>
                  <img src="/nitin-potdar-profile.png" alt="Nitin" style={s.aiAva} />
                  <div style={{ ...s.aiBubble, padding: '14px 18px' }}>
                    <div style={s.typing}>
                      {[0, 0.18, 0.36].map((d, i) => (
                        <span key={i} style={{ ...s.dot, animationDelay: `${d}s` }} />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div ref={bottomRef} />
          </div>
        </div>

        {/* Error */}
        {error && (
          <div style={s.errorBar}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            {error}
          </div>
        )}

        {/* In-app action suggestions */}
        {messages.length > 0 && messages.length % 3 === 0 && !loading && (
          <div style={s.actionBar}>
            <span style={s.actionLabel}>Try these tools in the app:</span>
            <div style={s.actionChips}>
              <Link to="/resume" style={s.actionChip}>Build Resume</Link>
              <Link to="/assessment" style={s.actionChip}>Practice Area Assessment</Link>
              <Link to="/mock-interview" style={s.actionChip}>Mock Interview</Link>
            </div>
          </div>
        )}

        {/* Input */}
        <form onSubmit={handleChatSubmit} style={s.inputArea}>
          <p style={{ ...s.wordCount, color: overLimit ? '#dc2626' : 'var(--text-3)' }}>
            {wordCount}/{WORD_LIMIT} words
          </p>
          <div style={s.inputWrap}>
            <textarea
              ref={textareaRef}
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={handleChatKeyDown}
              placeholder={isExhausted ? 'Token balance exhausted.' : 'Ask a follow-up question...'}
              style={{ ...s.textarea, opacity: isExhausted ? 0.5 : 1 }}
              rows={2}
              disabled={loading || isExhausted}
            />
            <button
              type="submit"
              disabled={loading || !chatInput.trim() || isExhausted || overLimit}
              style={{
                ...s.sendBtn,
                background: chatInput.trim() && !loading && !isExhausted && !overLimit ? mod.color : 'var(--border)',
                cursor: chatInput.trim() && !loading && !isExhausted && !overLimit ? 'pointer' : 'not-allowed',
              }}
            >
              {loading
                ? <span style={s.spinner2} />
                : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/>
                  </svg>
              }
            </button>
          </div>
          <p style={s.inputHint}>Enter to send</p>
        </form>

        {/* Toast */}
        {toast && <div style={s.toastMsg}>{toast}</div>}
      </div>
    </div>
  );
}

/* ── Styles ── */
const s = {
  page: {
    minHeight: 'calc(100dvh - 60px)', display: 'flex',
    flexDirection: 'column', padding: '16px',
    maxWidth: '880px', margin: '0 auto', width: '100%', boxSizing: 'border-box',
  },
  notFound: {
    flex: 1, display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center', gap: '12px',
  },
  backLink: { color: 'var(--navy)', fontWeight: '600', fontSize: '14px' },

  /* Chat container */
  chatContainer: {
    flex: 1, display: 'flex', flexDirection: 'column', position: 'relative',
    background: '#fff', border: '1px solid var(--border)',
    borderRadius: '16px', overflow: 'hidden', boxShadow: 'var(--shadow)',
    height: 'calc(100dvh - 92px)',
  },
  chatHeader: {
    padding: '14px 18px',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
  },
  chatHeaderLeft: { display: 'flex', alignItems: 'center', gap: '12px' },
  headerBackBtn: {
    background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: '8px', width: '32px', height: '32px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: '#fff', cursor: 'pointer', flexShrink: 0, textDecoration: 'none',
  },
  chatHeaderIcon: {
    width: '38px', height: '38px', borderRadius: '10px',
    background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.15)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff',
  },
  chatTitle: {
    fontFamily: "'Playfair Display', Georgia, serif",
    fontSize: '16px', fontWeight: '700', color: '#fff',
  },
  chatSubtitle: { fontSize: '12px', color: 'rgba(255,255,255,0.8)', marginTop: '2px', lineHeight: '1.5' },
  balancePill: {
    display: 'flex', alignItems: 'center', gap: '5px',
    background: 'rgba(255,255,255,0.07)', padding: '5px 12px',
    borderRadius: '99px', border: '1px solid rgba(255,255,255,0.1)',
  },
  balanceNum: { fontSize: '13px', fontWeight: '700' },
  balanceLabel: { fontSize: '11px', color: 'rgba(255,255,255,0.5)' },

  topicBar: {
    padding: '8px 18px', borderBottom: '1px solid var(--border-2)',
    background: 'var(--bg)',
  },
  topicLink: {
    fontSize: '12px', fontWeight: '600', color: 'var(--text-3)',
    textDecoration: 'none',
  },

  exhaustedBanner: {
    display: 'flex', gap: '10px', alignItems: 'flex-start',
    background: 'var(--danger-bg)', color: 'var(--danger)',
    borderBottom: '1px solid rgba(185,28,28,0.15)',
    padding: '12px 18px', fontSize: '13px',
  },
  chatArea: {
    flex: 1, overflowY: 'auto', display: 'flex',
    flexDirection: 'column', background: 'var(--bg)',
  },
  chatScroll: {
    flex: 1, overflowY: 'auto', padding: '20px 16px',
    display: 'flex', flexDirection: 'column', gap: '16px',
  },
  openingBlock: { display: 'flex', flexDirection: 'column', gap: '8px' },
  loadingBlock: { display: 'flex', flexDirection: 'column', gap: '8px' },

  aiRow: { display: 'flex', gap: '8px', alignItems: 'flex-start' },
  aiAva: {
    width: '32px', height: '32px', borderRadius: '50%',
    objectFit: 'cover', objectPosition: 'top center',
    flexShrink: 0, border: '1.5px solid var(--gold-mid)',
    boxShadow: '0 2px 8px rgba(12,35,64,0.12)',
  },
  aiBubble: {
    background: '#fff', border: '1px solid var(--border)',
    padding: '13px 16px', borderRadius: '4px 16px 16px 16px',
    maxWidth: '76%', boxShadow: 'var(--shadow-sm)',
  },

  userRow: { display: 'flex', justifyContent: 'flex-end' },
  userBubble: {
    background: '#0c2340', color: '#fff',
    padding: '12px 16px', borderRadius: '16px 4px 16px 16px',
    maxWidth: '76%',
  },

  mdWrapper: { fontSize: '14px', color: 'var(--text)' },
  mdP: { margin: '0 0 8px 0', lineHeight: '1.65', fontSize: '14px' },
  mdUl: { margin: '4px 0 8px 0', paddingLeft: '18px' },
  mdOl: { margin: '4px 0 8px 0', paddingLeft: '18px' },
  mdLi: { lineHeight: '1.65', marginBottom: '3px' },
  mdStrong: { fontWeight: '700', color: 'var(--navy)' },
  mdH: { fontSize: '14px', fontWeight: '700', margin: '10px 0 4px 0', color: 'var(--navy)', fontFamily: "'Playfair Display', Georgia, serif" },
  mdH3: { fontSize: '13px', fontWeight: '700', margin: '8px 0 4px 0', color: 'var(--navy)' },
  ts: { fontSize: '10px', color: 'var(--text-3)', display: 'block', marginTop: '8px', textAlign: 'right' },

  suggestRow: { paddingLeft: '38px', display: 'flex', flexDirection: 'column', gap: '6px' },
  suggestLabel: { fontSize: '10px', fontWeight: '600', color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.4px' },
  suggestChips: { display: 'flex', flexWrap: 'wrap', gap: '7px' },
  suggestChip: {
    background: '#fff', border: '1px solid var(--gold-mid)',
    color: 'var(--navy)', fontSize: '12px', fontWeight: '500',
    padding: '6px 13px', borderRadius: '99px', cursor: 'pointer',
    textAlign: 'left', lineHeight: '1.4',
  },

  skipBar: {
    padding: '8px 18px', borderTop: '1px solid var(--border-2)',
    background: '#fff', display: 'flex', justifyContent: 'flex-end',
  },
  skipBtn: {
    background: 'transparent', border: '1px solid',
    padding: '6px 16px', borderRadius: '99px',
    fontSize: '12px', fontWeight: '600', cursor: 'pointer',
  },

  errorBar: {
    margin: '0 14px 10px', background: 'var(--danger-bg)',
    border: '1px solid rgba(185,28,28,0.2)', color: 'var(--danger)',
    padding: '10px 14px', borderRadius: '10px',
    fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px',
  },
  actionBar: {
    padding: '10px 18px', borderTop: '1px solid var(--border-2)',
    background: '#fefce8', display: 'flex', flexDirection: 'column', gap: '6px',
  },
  actionLabel: {
    fontSize: '11px', fontWeight: '700', color: '#92400e',
    textTransform: 'uppercase', letterSpacing: '0.5px',
  },
  actionChips: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  actionChip: {
    background: '#fff', border: '1px solid #fbbf24',
    color: '#92400e', fontSize: '12px', fontWeight: '600',
    padding: '5px 12px', borderRadius: '99px', textDecoration: 'none',
  },
  inputArea: {
    padding: '12px 14px 14px', borderTop: '1px solid var(--border-2)',
    background: '#fff', display: 'flex', flexDirection: 'column', gap: '6px',
  },
  inputWrap: { display: 'flex', gap: '10px', alignItems: 'flex-end' },
  textarea: {
    flex: 1, padding: '11px 14px',
    border: '1px solid var(--border)', borderRadius: '10px',
    fontSize: '16px', resize: 'none', lineHeight: '1.5',
    background: 'var(--bg)', color: 'var(--text)',
    fontFamily: "'DM Sans', sans-serif",
    transition: 'border-color 0.15s, box-shadow 0.15s',
  },
  sendBtn: {
    width: '42px', height: '42px', color: '#fff', border: 'none',
    borderRadius: '10px', display: 'flex', alignItems: 'center',
    justifyContent: 'center', flexShrink: 0, transition: 'all 0.15s',
  },
  spinner2: {
    width: '14px', height: '14px', borderRadius: '50%',
    border: '2px solid rgba(255,255,255,0.3)', borderTop: '2px solid #fff',
    animation: 'spin 0.7s linear infinite',
  },
  inputHint: { fontSize: '10px', color: 'var(--text-3)', paddingLeft: '2px' },
  wordCount: { fontSize: '10px', fontWeight: '600', paddingLeft: '2px', margin: '0 0 4px 0' },
  toastMsg: {
    position: 'absolute', bottom: '90px', left: '50%', transform: 'translateX(-50%)',
    background: '#0c2340', color: '#fff',
    padding: '10px 20px', borderRadius: '10px', fontSize: '13px', fontWeight: '500',
    boxShadow: '0 4px 20px rgba(0,0,0,0.25)', zIndex: 100,
    animation: 'fadeIn 0.2s ease-out',
  },
  typing: { display: 'flex', gap: '4px', alignItems: 'center', height: '18px' },
  dot: {
    width: '7px', height: '7px', borderRadius: '50%',
    background: 'var(--gold)', opacity: 0.7,
    animation: 'bounce 1.2s infinite ease-in-out',
    display: 'inline-block',
  },
};
